import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  ICreateWorkspaceInvitationRequestInterfaces,
} from '@toolify/server/src/controllers/WorkspaceInvitationController/validators/CreateWorkspaceInvitation/types/ICreateWorkspaceInvitationRequestInterfaces'
import {
  IGetWorkspaceInvitationsRequestInterfaces,
} from '@toolify/server/src/controllers/WorkspaceInvitationController/validators/GetWorkspaceInvitations/types/IGetWorkspaceInvitationsRequestInterfaces'
import {
  IDeleteWorkspaceInvitationRequestInterfaces,
} from '@toolify/server/src/controllers/WorkspaceInvitationController/validators/DeleteWorkspaceInvitation/types/IDeleteWorkspaceInvitationRequestInterfaces'
import {
  IGetWorkspaceInvitationRequestInterfaces,
} from '@toolify/server/src/controllers/WorkspaceInvitationController/validators/GetWorkspaceInvitation/types/IGetWorkspaceInvitationRequestInterfaces'
import {
  IAcceptWorkspaceInvitationRequestInterfaces,
} from '@toolify/server/src/controllers/WorkspaceInvitationController/validators/AcceptWorkspaceInvitation/types/IAcceptWorkspaceInvitationRequestInterfaces'
import {
  mapWorkspaceInvitationResponseInstance,
} from '@toolify/client/src/modules/api/singletons/mapWorkspaceInvitationResponseInstance'
import {
  IUpdateWorkspaceInvitationRequestInterfaces,
} from '@toolify/server/src/controllers/WorkspaceInvitationController/validators/UpdateWorkspaceInvitation/types/IUpdateWorkspaceInvitationRequestInterfaces'

export const useWorkspaceInvitationApiStore = defineStore('workspaceInvitationApi', {
  actions: {
    async getInvitations(
      workspaceId: string,
    ): Promise<IGetWorkspaceInvitationsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {workspaceInvitations} = await apiRequestStore.get<IGetWorkspaceInvitationsRequestInterfaces>(`/workspace/${workspaceId}/invitations`)
      return {
        workspaceInvitations: workspaceInvitations.map(workspaceInvitation => {
          return mapWorkspaceInvitationResponseInstance(workspaceInvitation)
        }),
      }
    },

    async getInvitation(
      invitationId: string,
      invitationSecret: string,
    ): Promise<IGetWorkspaceInvitationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const res = await apiRequestStore.get<IGetWorkspaceInvitationRequestInterfaces>(`/workspace-invitation/${invitationId}-${invitationSecret}`)
      return {
        workspaceInvitation: mapWorkspaceInvitationResponseInstance(res.workspaceInvitation),
      }
    },

    async acceptInvitation(
      invitationId: string,
      invitationSecret: string,
    ): Promise<IAcceptWorkspaceInvitationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IAcceptWorkspaceInvitationRequestInterfaces>(`/workspace-invitation/${invitationId}-${invitationSecret}/accept`)
    },

    async updateInvitation(
      workspaceId: string,
      invitationId: string,
      groupIds: string[],
    ): Promise<IUpdateWorkspaceInvitationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.put<IUpdateWorkspaceInvitationRequestInterfaces>(`/workspace/${workspaceId}/invitation/${invitationId}`, {
        data: {
          groupIds,
        },
      })
    },

    async createInviteLink(
      workspaceId: string,
      groupIds: string[],
      expiresInMinutes?: number,
      maxUses?: number,
    ): Promise<ICreateWorkspaceInvitationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {url, expiresAt} = await apiRequestStore.post<ICreateWorkspaceInvitationRequestInterfaces>(`/workspace/${workspaceId}/invite`, {
        data: {
          groupIds,
          expiresInMinutes,
          maxUses,
        },
      })
      return {
        url,
        expiresAt,
      }
    },

    async inviteMultipleUsers(
      workspaceId: string,
      mailAddresses: string[],
      groupIds: string[],
    ): Promise<ICreateWorkspaceInvitationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const res = await apiRequestStore.post<ICreateWorkspaceInvitationRequestInterfaces>(`/workspace/${workspaceId}/invite`, {
        data: {
          mailAddresses,
          groupIds,
        },
      })
      return {
        workspaceInvitations: (res.workspaceInvitations ?? []).map(workspaceInvitation => {
          return mapWorkspaceInvitationResponseInstance(workspaceInvitation)
        }),
        inviteResults: res.inviteResults ?? [],
      }
    },

    async deleteInvitation(
      workspaceId: string,
      invitationId: string,
    ): Promise<IDeleteWorkspaceInvitationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.delete<IDeleteWorkspaceInvitationRequestInterfaces>(`/workspace/${workspaceId}/invitation/${invitationId}`)
    },
  },
})
